import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Money-movement actions are admin-only (the backend enforces this via
// requireSuperAdmin; the UI hides the buttons to match).
const currentUser = (() => {
  try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}'); }
  catch { return {}; }
})();
const isAdmin = currentUser?.role === 'admin';

const fmt = (cents) =>
  '$' + (Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};

const WIRE_STATUS = {
  pending_approval: { bg: '#f59e0b', color: '#1a1a1a', label: 'Pending Approval' },
  approved:         { bg: '#3a6ad9', color: '#ffffff', label: 'Approved' },
  sent:             { bg: '#acf44e', color: '#1a1a1a', label: 'Sent' },
  rejected:         { bg: '#ff082f', color: '#ffffff', label: 'Rejected' },
};

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px', marginBottom: 12 };
const empty = (msg) => (
  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>{msg}</div>
);

export default function AdminWireRequests() {
  const [wires, setWires]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [processing, setProcessing] = useState({});
  // OTP-gated wire approval — modal opens once /request-otp succeeds.
  const [otpWireId, setOtpWireId]       = useState(null);
  const [otpCode, setOtpCode]           = useState('');
  const [otpSending, setOtpSending]     = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError]         = useState('');
  const [otpExpiry, setOtpExpiry]       = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` };

  const fetchWires = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/agents/finance/wire-requests`, { headers });
      setWires(res.data || []);
    } catch (err) {
      console.error('Wire requests fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWires(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWire = async (id, action, reason) => {
    setProcessing(p => ({ ...p, [id]: true }));
    try {
      await axios.patch(
        `${API}/api/admin/agents/finance/wire-requests/${id}/${action}`,
        action === 'reject' ? { reason } : {},
        { headers }
      );
      await fetchWires();
    } catch (err) {
      alert(err?.response?.data?.error || err.message || 'Action failed.');
    } finally {
      setProcessing(p => ({ ...p, [id]: false }));
    }
  };

  // Two-step wire approval. Step 1 requests an emailed OTP and opens the modal;
  // step 2 verifies it server-side — the only path that actually approves.
  const handleWireApprove = async (id) => {
    setOtpSending(true);
    setOtpError('');
    try {
      const res = await axios.post(
        `${API}/api/admin/agents/finance/wire-requests/${id}/request-otp`,
        {}, { headers }
      );
      setOtpWireId(id);
      setOtpExpiry(res.data.expiresAt);
      setOtpCode('');
    } catch (err) {
      // Modal isn't open yet, so surface via alert.
      alert(err?.response?.data?.error || err.message || 'Could not send approval code.');
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpVerify = async () => {
    if (otpCode.length !== 6) return;
    setOtpVerifying(true);
    setOtpError('');
    try {
      await axios.post(
        `${API}/api/admin/agents/finance/wire-requests/${otpWireId}/verify-and-approve`,
        { code: otpCode }, { headers }
      );
      setOtpWireId(null);
      setOtpCode('');
      await fetchWires();
    } catch (err) {
      setOtpError(err.response?.data?.error || err.message);
    } finally {
      setOtpVerifying(false);
    }
  };

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>Finance</div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Wire Requests</h1>
          </div>
          {!isAdmin && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#92400e', fontWeight: 600 }}>
              View-only — wire approvals are restricted to admins
            </div>
          )}
        </div>

        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
          Wires can only be sent to verified payees on the <strong>Recipients</strong> tab.
        </div>

        {loading && <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>Loading…</div>}

        {!loading && wires.length === 0 && empty('No wire requests')}
        {!loading && wires.map(w => {
          const sc = WIRE_STATUS[w.status] || WIRE_STATUS.pending_approval;
          const busy = !!processing[w.id];
          return (
            <div key={w.id} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{fmt(w.amount_cents)}</div>
                  <div style={{ fontSize: 13, color: '#111827', fontWeight: 600, marginTop: 2 }}>{w.recipient_name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>
                    {w.recipient_bank || 'Bank on file'} · acct ••••{w.account_number_last4 || '----'}
                  </div>
                  {w.memo && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{w.memo}</div>}
                  {w.loan_borrower && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Loan: {w.loan_borrower}</div>}
                  {w.status === 'rejected' && w.rejected_reason && (
                    <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>Rejected: {w.rejected_reason}</div>
                  )}
                  {w.status === 'sent' && <div style={{ fontSize: 11, color: '#166534', marginTop: 4 }}>Sent {fmtDate(w.sent_at)}</div>}
                  {w.status === 'approved' && <div style={{ fontSize: 11, color: '#1e40af', marginTop: 4 }}>Approved {fmtDate(w.approved_at)}</div>}
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>{sc.label}</span>
              </div>
              {isAdmin && (w.status === 'pending_approval' || w.status === 'approved') && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  {w.status === 'pending_approval' && (
                    <button disabled={busy || otpSending} onClick={() => handleWireApprove(w.id)}
                      style={{ background: (busy || otpSending) ? '#9ca3af' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: (busy || otpSending) ? 'default' : 'pointer' }}>
                      {otpSending ? 'Sending code…' : 'Approve'}
                    </button>
                  )}
                  {w.status === 'approved' && (
                    <button disabled={busy} onClick={() => { if (window.confirm('Mark this wire as SENT? Confirm the funds have actually moved.')) handleWire(w.id, 'sent'); }}
                      style={{ background: busy ? '#9ca3af' : '#16a34a', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: busy ? 'default' : 'pointer' }}>
                      Mark Sent
                    </button>
                  )}
                  <button disabled={busy} onClick={() => { const reason = window.prompt('Reason for rejection (optional):'); if (reason !== null) handleWire(w.id, 'reject', reason); }}
                    style={{ background: '#fff', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: busy ? 'default' : 'pointer' }}>
                    Reject
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {otpWireId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '32px', width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
              Verify Wire Approval
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
              A 6-digit code has been emailed to your inbox. Enter it below to approve this wire transfer.
            </div>
            <input
              type="text" inputMode="numeric" maxLength={6}
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => { if (e.key === 'Enter' && otpCode.length === 6 && !otpVerifying) handleOtpVerify(); }}
              placeholder="000000" autoFocus
              style={{ width: '100%', textAlign: 'center', fontSize: 28, fontWeight: 700, letterSpacing: 12, fontFamily: 'monospace', padding: '14px', border: '2px solid #e5e7eb', borderRadius: 10, boxSizing: 'border-box', marginBottom: 12, color: '#111827' }}
            />
            {otpError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#991b1b', marginBottom: 12 }}>
                {otpError}
              </div>
            )}
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 16, textAlign: 'center' }}>
              Code expires at {otpExpiry ? new Date(otpExpiry).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button disabled={otpVerifying || otpCode.length !== 6} onClick={handleOtpVerify}
                style={{ flex: 1, background: (otpVerifying || otpCode.length !== 6) ? '#9ca3af' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 600, cursor: (otpVerifying || otpCode.length !== 6) ? 'default' : 'pointer' }}>
                {otpVerifying ? 'Verifying…' : 'Verify & Approve'}
              </button>
              <button onClick={() => { setOtpWireId(null); setOtpCode(''); setOtpError(''); }}
                style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
